import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Search, ShoppingBag, Plus, SlidersHorizontal, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useShop } from '../context/ShopContext';
import { useFavorites } from '../hooks/useFavorites';
import { useTheme } from '../context/ThemeContext';
import Header from '../components/Header';
import ShareButtons from '../components/ShareButtons';
import Footer from '../components/Footer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export default function ShopPage() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState(['All']);
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [loading, setLoading] = useState(true);
    const { addToCart } = useCart();
    const { merchantId, shopName, shopDescription, logoUrl, shopUrl } = useShop();
    const { isFavorite, toggleFavorite } = useFavorites(merchantId);
    const { themeData } = useTheme();
    const customization = themeData?.customization || {};
    const productsPerRow = customization.products_per_row ?? 4;
    const gridClass =
        productsPerRow === 2
            ? 'grid-cols-2 gap-4 sm:gap-6'
            : productsPerRow === 3
              ? 'grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6'
              : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6';

    const fetchCategories = useCallback(async () => {
        if (!merchantId) return;
        try {
            const response = await axios.get(`${API_URL}/storefront/categories?owner_id=${merchantId}`);
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    }, [merchantId]);

    const fetchProducts = useCallback(async () => {
        if (!merchantId) return;
        try {
            setLoading(true);
            const params = new URLSearchParams();
            params.append('owner_id', merchantId);
            if (searchQuery) params.append('search', searchQuery);
            if (selectedCategory && selectedCategory !== 'All') params.append('category', selectedCategory);
            const sort = customization.default_sort || 'newest';
            params.append('sort', sort);
            const response = await axios.get(`${API_URL}/products?${params.toString()}`);
            setProducts(response.data);
        } catch (error) {
            console.error('Failed to fetch products:', error);
        } finally {
            setLoading(false);
        }
    }, [merchantId, searchQuery, selectedCategory, customization.default_sort]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleAddToCart = async (product) => {
        await addToCart(product);
    };

    return (
        <div className="min-h-screen min-h-[100dvh] bg-background">
            {/* Dynamic SEO Meta Tags for Shop Homepage */}
            <Helmet>
                <title>{shopName} - Online Shop</title>
                <meta name="description" content={shopDescription || `Ανακαλύψτε μοναδικά προϊόντα στο ${shopName}. Γρήγορη αποστολή σε όλη την Ελλάδα.`} />
                <meta name="robots" content="index, follow" />
                {shopUrl && <link rel="canonical" href={shopUrl} />}

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content={shopName} />
                <meta property="og:title" content={`${shopName} - Online Shop`} />
                <meta property="og:description" content={shopDescription || `Ανακαλύψτε μοναδικά προϊόντα στο ${shopName}`} />
                {logoUrl && <meta property="og:image" content={logoUrl.startsWith('/api') ? `${API_URL.replace('/api', '')}${logoUrl}` : logoUrl} />}
                <meta property="og:url" content={shopUrl || window.location.href} />
                <meta property="og:locale" content="el_GR" />

                {/* Twitter Card */}
                <meta name="twitter:card" content={logoUrl ? 'summary_large_image' : 'summary'} />
                <meta name="twitter:title" content={`${shopName} - Online Shop`} />
                <meta name="twitter:description" content={shopDescription || `Ανακαλύψτε μοναδικά προϊόντα στο ${shopName}`} />
                {logoUrl && <meta name="twitter:image" content={logoUrl.startsWith('/api') ? `${API_URL.replace('/api', '')}${logoUrl}` : logoUrl} />}

                {/* JSON-LD: LocalBusiness for Google */}
                {shopUrl && (
                    <script type="application/ld+json">
                        {JSON.stringify({
                            '@context': 'https://schema.org',
                            '@type': 'Organization',
                            name: shopName,
                            description: shopDescription || `${shopName} - Online Shop. Ανακαλύψτε μοναδικά προϊόντα.`,
                            url: shopUrl,
                            ...(logoUrl && { logo: logoUrl.startsWith('/api') ? `${API_URL.replace('/api', '')}${logoUrl}` : logoUrl })
                        })}
                    </script>
                )}
            </Helmet>

            <Header />

            {/* Hero image (optional) - full width, centered */}
            {customization.hero_image_url && (
                <div className="w-full -mt-2 mb-6">
                    <div className="aspect-[3/1] max-h-64 w-full overflow-hidden bg-muted">
                        <img
                            src={customization.hero_image_url.startsWith('http') ? customization.hero_image_url : `${API_URL.replace('/api', '')}${customization.hero_image_url}`}
                            alt=""
                            className="w-full h-full object-cover object-center"
                        />
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12">
                <div className="mb-6 sm:mb-8">
                    <h1 className="font-display text-3xl sm:text-4xl font-semibold text-primary mb-2 tracking-tight">
                        {shopName}
                    </h1>
                    {(customization.tagline || shopDescription) && (
                        <p className="text-muted-foreground">{customization.tagline || shopDescription}</p>
                    )}
                    <div className="mt-3">
                        <ShareButtons 
                            url={window.location.href}
                            title={`${shopName} - Online Shop`}
                            description={shopDescription}
                            type="shop"
                        />
                    </div>
                </div>

                {/* Search & Category Filter */}
                <div className="flex flex-col gap-4 mb-8">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Αναζήτηση προϊόντων..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10 h-11"
                            data-testid="search-input"
                        />
                    </div>

                    {categories.length > 1 && (
                        <div className="flex items-center gap-2 overflow-x-auto pb-1 -mx-1 px-1 scrollbar-hide scroll-touch touch-manipulation" data-testid="category-filter">
                            <SlidersHorizontal className="w-4 h-4 text-muted-foreground shrink-0" />
                            {categories.map((cat) => (
                                <button
                                    key={cat}
                                    onClick={() => setSelectedCategory(cat)}
                                    data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                                    className={`px-4 py-2 sm:py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all touch-manipulation active:scale-[0.98] ${
                                        selectedCategory === cat
                                            ? 'bg-primary text-primary-foreground shadow-sm'
                                            : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
                                    }`}
                                >
                                    {cat === 'All' ? 'Όλα' : cat}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Products Grid */}
                {loading ? (
                    <div className={`grid gap-6 ${gridClass}`}>
                        {[...Array(8)].map((_, i) => (
                            <div key={i} className="animate-pulse">
                                <div className="aspect-square bg-gray-200 rounded-2xl mb-3"></div>
                                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                            </div>
                        ))}
                    </div>
                ) : products.length === 0 ? (
                    <div className="text-center py-12">
                        <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                        <h2 className="font-display text-2xl font-semibold text-primary mb-2">
                            {searchQuery ? (customization.empty_messages?.no_search_results || 'Δεν βρέθηκαν αποτελέσματα.') : (customization.empty_messages?.no_products || 'Δεν υπάρχουν προϊόντα ακόμα.')}
                        </h2>
                        <p className="text-muted-foreground">{searchQuery ? '' : 'Επιστρέψτε αργότερα.'}</p>
                    </div>
                ) : (
                    <div className={`grid ${gridClass}`}>
                        {products.map((product, index) => (
                            <motion.div
                                key={product.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className="group"
                            >
                                <div className="relative mb-3">
                                    <Link to={`/product/${product.id}`} data-testid={`product-card-${product.id}`}>
                                        <div className="aspect-square rounded-xl sm:rounded-2xl overflow-hidden bg-gray-100">
                                            <img
                                                src={product.image_url?.startsWith('/api') ? `${API_URL.replace('/api', '')}${product.image_url}` : product.image_url}
                                                alt={product.name}
                                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                            />
                                        </div>
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={() => toggleFavorite(product.id)}
                                        className={`absolute top-2 right-2 h-9 w-9 rounded-full border bg-background/90 shadow-sm flex items-center justify-center transition hover:bg-background ${
                                            isFavorite(product.id) ? 'text-red-600 border-red-200 bg-red-50/95' : 'text-muted-foreground'
                                        }`}
                                        aria-label={isFavorite(product.id) ? 'Αφαίρεση από αγαπημένα' : 'Αγαπημένο'}
                                        data-testid={`favorite-toggle-${product.id}`}
                                    >
                                        <Heart className={`w-4 h-4 ${isFavorite(product.id) ? 'fill-current' : ''}`} />
                                    </button>
                                </div>
                                <Link to={`/product/${product.id}`}>
                                    <h3 className="font-medium text-primary truncate">{product.name}</h3>
                                    <p className="text-lg font-semibold">€{product.price.toFixed(2)}</p>
                                </Link>
                                <Button
                                    onClick={() => handleAddToCart(product)}
                                    variant="outline"
                                    size="sm"
                                    className="w-full mt-2 rounded-full h-11 sm:h-9 text-sm"
                                    data-testid={`add-to-cart-${product.id}`}
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add to Cart
                                </Button>
                            </motion.div>
                        ))}
                    </div>
                )}

                {customization.trust_badges?.length > 0 && (
                    <div className="mt-12 pt-8 border-t border-border">
                        <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                            {customization.trust_badges.includes('secure_payment') && <span>🔒 Ασφαλής πληρωμή</span>}
                            {customization.trust_badges.includes('fast_shipping') && <span>🚚 Γρήγορη αποστολή</span>}
                            {customization.trust_badges.includes('returns') && <span>↩️ Επιστροφές</span>}
                            {customization.trust_badges.includes('support') && <span>💬 Υποστήριξη</span>}
                        </div>
                    </div>
                )}
            </main>

            <Footer shopName={shopName} />
        </div>
    );
}
